import {
  OrderSummaryCartSummary,
  TOrderSummary,
} from 'typescript/types/api/solidshop';
import {
  BillingCycleUnit,
  ReceiptItem,
} from 'typescript/apps/checkout_v3/components/order_summary/components/receipt_item';
import { toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import { toCurrency } from 'typescript/utilities/currency';
import {
  getKioskPricingContext,
  sortProductByHardwareOrSoftware,
  sumSoftwarePricing,
} from 'utilities/software';
import { useTranslation } from 'react-i18next';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  useGetOrderSummary,
  useUpdateOrderAddressInformation,
} from 'typescript/hooks/use_order_actions_client';
import { useSelectShippingRate } from 'typescript/hooks/use_shipping_rates_client';
import { useSelectOffer } from 'typescript/hooks/use_offers_client';
import useShowLoading from 'typescript/apps/checkout_v3/components/order_summary/components/use_show_loading';

export const ORDER_SUMMARY_SUBTOTALS_TEST_ID = 'order-summary-subtotals';

type SubtotalsProps = {
  orderSummaryData: TOrderSummary;
};

export const Subtotals = ({ orderSummaryData }: SubtotalsProps) => {
  const { t } = useTranslation();
  const { orderNumber } = useCheckoutFlow();
  const { showShippingLoading, showTaxLoading, showInstallmentsLoading } =
    useShowLoading();

  const { isAnyPending: isUpdatingOrderAddress } =
    useUpdateOrderAddressInformation({
      orderNumber,
    });
  const { isAnyPending: isUpdatingShippingRate } = useSelectShippingRate({
    orderNumber,
  });
  const { isAnyPending: isUpdatingOffers } = useSelectOffer({
    orderNumber,
  });
  const { isFetching: orderDataIsFetching } = useGetOrderSummary({
    orderNumber,
  });

  const { software: softwareProducts } = sortProductByHardwareOrSoftware(
    orderSummaryData.cart_summary,
  );

  const { isPnpSubscribed, locationCount, kioskDeviceCount, k1aDeviceCount } =
    getKioskPricingContext(orderSummaryData);

  const installmentSummary = orderSummaryData.installment_summary;
  const isInstallment =
    orderSummaryData.payment_due.payment_schedule === 'installment';

  const softwareSubtotal = sumSoftwarePricing(
    softwareProducts
      .map((cartItem: OrderSummaryCartSummary) => cartItem.plan)
      .filter(Boolean),
    { isPnpSubscribed, locationCount, kioskDeviceCount, k1aDeviceCount },
  );

  const sameFinalPayment =
    installmentSummary?.final_payment_amount?.amount ===
    installmentSummary?.payment_amount?.amount;

  return (
    <div data-testid={ORDER_SUMMARY_SUBTOTALS_TEST_ID}>
      {/* TODO: improve naive calculation that doesn't take renewal cycle or varying pricing models into consideration */}
      {softwareSubtotal.amount > 0 && (
        <ReceiptItem
          label={t('checkout.receipt.label.v3.subscriptionsSubtotal')}
          override={toCurrencyWithMinorUnitAmount(
            softwareSubtotal.amount,
            softwareSubtotal.currencyCode,
          )}
          billingCycle={BillingCycleUnit.MONTH}
          tooltip={t('checkout.receipt.label.v3.subscriptionsSubtotalSubtext')}
        />
      )}
      {/* TODO: implement 'Subscription fees' row when able to get the fee-per-tx rate after P&P feature rolled out */}
      {/* TODO: implement 'Software subtotal' row when able to get software upfront amount */}
      {isInstallment && (
        <ReceiptItem
          label={t('checkout.receipt.label.v3.itemFinancing')}
          cost={installmentSummary.payment_amount.amount}
          currency={installmentSummary.payment_amount.currency_code}
          billingCycle={BillingCycleUnit.MONTH}
          tooltip={t(
            sameFinalPayment
              ? t('checkout.receipt.label.v3.itemFinancingSubtext')
              : t('checkout.receipt.label.v3.itemFinancingVaryingSubtext'),
            {
              numMonths: sameFinalPayment
                ? installmentSummary.number_payment_periods
                : installmentSummary.number_payment_periods - 1,
              monthlyPaymentAmount: toCurrency({
                amount: Number.parseFloat(
                  installmentSummary.payment_amount.amount,
                ),
                currency: installmentSummary.payment_amount.currency_code,
              }),
              finalPaymentAmount:
                installmentSummary.final_payment_amount &&
                toCurrency({
                  amount: Number.parseFloat(
                    installmentSummary.final_payment_amount.amount,
                  ),
                  currency:
                    installmentSummary.final_payment_amount.currency_code,
                }),
              feeAmount: toCurrency({
                amount: Number.parseFloat(installmentSummary.fee_amount.amount),
                currency: installmentSummary.fee_amount.currency_code,
              }),
            },
          )}
          isLoading={
            showInstallmentsLoading && (isUpdatingOffers || orderDataIsFetching)
          }
        />
      )}
      <ReceiptItem
        label={t('checkout.receipt.label.v3.itemSubtotal')}
        cost={orderSummaryData.subtotal.amount}
        currency={orderSummaryData.subtotal?.currency}
      />
      <ReceiptItem
        label={t('checkout.receipt.label.v3.shippingSubtotal')}
        cost={orderSummaryData.shipping.amount}
        currency={orderSummaryData.shipping.currency}
        isShipping={true}
        isLoading={
          showShippingLoading &&
          (isUpdatingOrderAddress ||
            isUpdatingShippingRate ||
            orderDataIsFetching)
        }
      />
      {orderSummaryData.additional_tax?.amount &&
        orderSummaryData.additional_tax.currency && (
          <ReceiptItem
            label={
              orderSummaryData.shipping_address?.country_code
                ? t('checkout.receipt.label.v3.taxSubtotal')
                : t('checkout.receipt.label.v3.estimatedTaxSubtotal')
            }
            cost={orderSummaryData.additional_tax.amount}
            currency={orderSummaryData.additional_tax.currency}
            isLoading={
              showTaxLoading &&
              (isUpdatingOrderAddress ||
                isUpdatingShippingRate ||
                orderDataIsFetching)
            }
          />
        )}
    </div>
  );
};
